/* Upkick: Video carousel
 * Native scroll-snap carousel with optional infinite loop, muted autoplay while on screen,
 * and a per-card sound toggle for Shopify-hosted, YouTube and Vimeo videos.
 */
if (!customElements.get('upkick-video-carousel')) {
  class UpkickVideoCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      this.autoplay = this.dataset.autoplay === 'true'
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.originals = Array.from(this.track.querySelectorAll(':scope > [data-slide]'));
      this.count = this.originals.length;
      this.loop = this.dataset.loop === 'true' && this.count > 2;
      this.dataset.loop = this.loop ? 'true' : 'false';
      this.soundSlide = null;

      if (this.loop) this.buildClones();
      this.slides = Array.from(this.track.querySelectorAll(':scope > [data-slide]'));

      this.bindSound();
      this.bindDrag();
      this.bindArrows();
      this.bindScroll();
      this.bindEditor();
      this.observe();

      requestAnimationFrame(() => {
        this.setAttribute('data-ready', '');
        if (this.loop) this.centerOn(this.originals[0], 'instant');
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    /* ---------- Loop ---------- */

    buildClones() {
      const makeClone = (slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-clone', '');
        clone.removeAttribute('data-shopify-editor-block');
        // Clones stay clickable (they are on screen) but out of the tab order.
        clone.querySelectorAll('button, iframe').forEach((el) => el.setAttribute('tabindex', '-1'));
        return clone;
      };
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      this.originals.forEach((slide) => {
        before.appendChild(makeClone(slide));
        after.appendChild(makeClone(slide));
      });
      this.track.prepend(before);
      this.track.append(after);
    }

    get setWidth() {
      return this.slides[this.count * 2].offsetLeft - this.slides[this.count].offsetLeft;
    }

    scrollPosFor(slide) {
      return slide.offsetLeft - this.track.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
    }

    centerOn(slide, behavior = 'smooth') {
      if (!slide) return;
      this.track.scrollTo({ left: this.scrollPosFor(slide), behavior });
    }

    // Keep the scroll position inside the middle (original) set so the loop never runs out.
    wrap() {
      if (!this.loop || this.dragging) return;
      const home = this.scrollPosFor(this.originals[0]);
      const width = this.setWidth;
      const pos = this.track.scrollLeft;
      let shift = 0;
      if (pos < home - width / 2) shift = width;
      else if (pos >= home + width / 2) shift = -width;
      if (!shift) return;

      const offset = shift > 0 ? this.count : -this.count;
      const hadSound = this.soundSlide;
      // Carry playback position across to the counterpart so the jump is invisible.
      const handoff = this.slides
        .map((slide, i) => ({ slide, twin: this.slides[i + offset] }))
        .filter(({ slide, twin }) => twin && slide.dataset.kind === 'native' && this.isPlaying(slide));

      this.track.style.scrollSnapType = 'none';
      this.track.scrollLeft = pos + shift;
      this.track.style.scrollSnapType = '';

      handoff.forEach(({ slide, twin }) => {
        const from = slide.querySelector('video');
        const to = twin.querySelector('video');
        if (from && to) {
          try { to.currentTime = from.currentTime; } catch (e) { /* not seekable yet */ }
        }
      });
      if (hadSound) {
        const twin = this.slides[this.slides.indexOf(hadSound) + offset];
        if (twin) this.setSound(twin);
      }
    }

    bindScroll() {
      let timer;
      const settle = () => this.wrap();
      if ('onscrollend' in window) {
        this.track.addEventListener('scrollend', settle);
      } else {
        this.track.addEventListener('scroll', () => {
          clearTimeout(timer);
          timer = setTimeout(settle, 150);
        }, { passive: true });
      }
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          if (this.loop) this.centerOn(this.nearestSlide(), 'instant');
        }, 150);
      });
    }

    nearestSlide() {
      const mid = this.track.scrollLeft + this.track.clientWidth / 2 + this.track.offsetLeft;
      let best = this.slides[0];
      let bestDist = Infinity;
      this.slides.forEach((slide) => {
        const dist = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - mid);
        if (dist < bestDist) { bestDist = dist; best = slide; }
      });
      return best;
    }

    /* ---------- Controls ---------- */

    bindArrows() {
      const step = (dir) => {
        const current = this.slides.indexOf(this.nearestSlide());
        const target = this.slides[Math.max(0, Math.min(this.slides.length - 1, current + dir))];
        this.centerOn(target);
      };
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));
    }

    // Mouse drag on desktop. Touch uses native scrolling.
    bindDrag() {
      let startX = 0;
      let startScroll = 0;
      this.moved = false;

      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        this.dragging = true;
        this.moved = false;
        startX = e.clientX;
        startScroll = this.track.scrollLeft;
        this.track.classList.add('is-dragging');
      });
      window.addEventListener('pointermove', (e) => {
        if (!this.dragging) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 5) this.moved = true;
        this.track.scrollLeft = startScroll - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!this.dragging) return;
        this.dragging = false;
        this.track.classList.remove('is-dragging');
        this.centerOn(this.nearestSlide());
      });
      // Swallow the click that ends a drag so it doesn't toggle sound.
      this.track.addEventListener('click', (e) => {
        if (this.moved) {
          e.preventDefault();
          e.stopPropagation();
          this.moved = false;
        }
      }, true);
      this.track.addEventListener('dragstart', (e) => e.preventDefault());
    }

    bindSound() {
      this.track.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-sound]');
        if (!btn) return;
        const slide = btn.closest('[data-slide]');
        if (slide === this.soundSlide) this.clearSound();
        else this.setSound(slide);
      });
    }

    setSound(slide) {
      if (this.soundSlide && this.soundSlide !== slide) this.clearSound();
      this.soundSlide = slide;
      slide.classList.add('has-sound');
      const btn = slide.querySelector('[data-sound]');
      if (btn) btn.setAttribute('aria-pressed', 'true');
      this.play(slide, true);
    }

    clearSound() {
      const slide = this.soundSlide;
      if (!slide) return;
      this.soundSlide = null;
      slide.classList.remove('has-sound');
      const btn = slide.querySelector('[data-sound]');
      if (btn) btn.setAttribute('aria-pressed', 'false');
      if (this.autoplay && slide.dataset.visible === 'true') this.play(slide, false);
      else this.pause(slide);
    }

    /* ---------- Playback ---------- */

    observe() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const slide = entry.target;
          const visible = entry.isIntersecting;
          slide.dataset.visible = visible ? 'true' : 'false';
          if (visible) {
            if (this.autoplay || slide === this.soundSlide) this.play(slide, slide === this.soundSlide);
          } else {
            if (slide === this.soundSlide) this.clearSound();
            this.pause(slide);
          }
        });
      }, { threshold: 0.35 });
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    isPlaying(slide) {
      const video = slide.querySelector('video');
      return !!video && !video.paused;
    }

    play(slide, withSound) {
      const kind = slide.dataset.kind;
      if (kind === 'native') {
        const video = slide.querySelector('video');
        if (!video) return;
        video.muted = !withSound;
        if (video.preload === 'none') video.preload = 'auto';
        const p = video.play();
        if (p && p.catch) {
          p.catch(() => {
            // Browser blocked sound: fall back to muted playback.
            if (!video.muted) { video.muted = true; video.play().catch(() => {}); }
          });
        }
      } else if (kind === 'youtube' || kind === 'vimeo') {
        slide.dataset.wantSound = withSound ? 'true' : 'false';
        slide.dataset.wantPlay = 'true';
        this.loadExternal(slide);
        this.syncExternal(slide);
      }
    }

    pause(slide) {
      const kind = slide.dataset.kind;
      if (kind === 'native') {
        const video = slide.querySelector('video');
        if (video && !video.paused) video.pause();
      } else if (kind === 'youtube' || kind === 'vimeo') {
        slide.dataset.wantPlay = 'false';
        slide.dataset.wantSound = 'false';
        this.syncExternal(slide);
      }
    }

    loadExternal(slide) {
      const iframe = slide.querySelector('iframe[data-src]');
      if (!iframe || iframe.src) return;
      const kind = slide.dataset.kind;
      const url = new URL(iframe.dataset.src);
      url.searchParams.set('autoplay', '1');
      if (kind === 'youtube') {
        url.searchParams.set('mute', slide.dataset.wantSound === 'true' ? '0' : '1');
        url.searchParams.set('origin', window.location.origin);
      } else {
        url.searchParams.set('muted', slide.dataset.wantSound === 'true' ? '0' : '1');
      }
      iframe.addEventListener('load', () => {
        slide.dataset.loaded = 'true';
        // Give the player a moment to initialise before sending commands.
        setTimeout(() => {
          slide.classList.add('is-external-ready');
          this.syncExternal(slide);
        }, 600);
      }, { once: true });
      iframe.src = url.toString();
    }

    syncExternal(slide) {
      if (slide.dataset.loaded !== 'true') return;
      const iframe = slide.querySelector('iframe');
      if (!iframe || !iframe.contentWindow) return;
      const play = slide.dataset.wantPlay === 'true';
      const sound = slide.dataset.wantSound === 'true';

      if (slide.dataset.kind === 'youtube') {
        const send = (func, args = []) => iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func, args }), '*'
        );
        send(sound ? 'unMute' : 'mute');
        if (sound) send('setVolume', [100]);
        send(play ? 'playVideo' : 'pauseVideo');
      } else {
        const send = (method, value) => iframe.contentWindow.postMessage(
          JSON.stringify(value === undefined ? { method } : { method, value }), '*'
        );
        send('setMuted', !sound);
        send('setVolume', sound ? 1 : 0);
        send(play ? 'play' : 'pause');
      }
    }

    /* ---------- Theme editor ---------- */

    bindEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      document.addEventListener('shopify:block:select', (e) => {
        const slide = this.originals.find((s) => s.contains(e.target) || s === e.target);
        if (slide) this.centerOn(slide);
      });
    }
  }

  customElements.define('upkick-video-carousel', UpkickVideoCarousel);
}
